"use client"

import { useState } from "react"
import { Loader2, X } from "lucide-react"
import Image from "next/image"
import { updateCartItemCustomization } from "@lib/data/cart"
import { convertToLocale } from "@lib/util/money"
import {
  CUSTOMIZATION_LIMITS,
  CUSTOMIZATION_PATCHES,
  CUSTOMIZATION_PRICING,
} from "@constants/product-page"
import { HttpTypes } from "@medusajs/types"

type LineItemCustomizationEditorProps = {
  item: HttpTypes.StoreCartLineItem
  currencyCode: string
  onClose: () => void
}

/**
 * Inline "edit customisation" panel, expanded in place within a cart line
 * item — matches the reference site's cart drawer (Name/Number fields +
 * Update button, no navigation) instead of linking out to the product page.
 */
export default function LineItemCustomizationEditor({
  item,
  currencyCode,
  onClose,
}: LineItemCustomizationEditorProps) {
  const metadata = (item.metadata as Record<string, unknown> | null) ?? null

  const [customName, setCustomName] = useState(
    typeof metadata?.custom_name === "string" ? metadata.custom_name : ""
  )
  const [customNumber, setCustomNumber] = useState(
    typeof metadata?.custom_number === "string" ? metadata.custom_number : ""
  )
  const [selectedPatch, setSelectedPatch] = useState(
    typeof metadata?.patch_id === "string" && metadata.patch_id ? metadata.patch_id : "none"
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const formatPrice = (amount: number) =>
    convertToLocale({ amount, currency_code: currencyCode })

  const handleUpdate = async () => {
    setSaving(true)
    setError(null)

    const result = await updateCartItemCustomization({
      lineItemId: item.id,
      customName,
      customNumber,
      patchId: selectedPatch,
    })

    setSaving(false)

    if (!result.success) {
      setError(result.error || "Failed to update customisation")
      return
    }

    onClose()
  }

  return (
    <div className="mt-2.5 p-3 bg-gray-50 rounded-xl space-y-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-bold text-brand-navy">
          Name &amp; Number ( +{formatPrice(CUSTOMIZATION_PRICING.nameAndNumber)} )
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          value={customName}
          onChange={(e) =>
            setCustomName(e.target.value.slice(0, CUSTOMIZATION_LIMITS.nameMaxLength))
          }
          placeholder="Name"
          maxLength={CUSTOMIZATION_LIMITS.nameMaxLength}
          aria-label="Custom name"
          className="w-full px-3 py-2 text-xs font-medium text-brand-navy bg-white border border-gray-200 rounded-lg outline-none focus:border-brand-navy transition-colors"
        />
        <input
          type="text"
          value={customNumber}
          onChange={(e) =>
            setCustomNumber(
              e.target.value.slice(0, CUSTOMIZATION_LIMITS.numberMaxLength).replace(/[^0-9]/g, "")
            )
          }
          placeholder="Number"
          maxLength={CUSTOMIZATION_LIMITS.numberMaxLength}
          aria-label="Custom number"
          className="w-full px-3 py-2 text-xs font-medium text-brand-navy bg-white border border-gray-200 rounded-lg outline-none focus:border-brand-navy transition-colors"
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500 flex-shrink-0">Patch:</span>
        {CUSTOMIZATION_PATCHES.map((patch) => {
          const isSelected = selectedPatch === patch.id
          return (
            <button
              key={patch.id}
              type="button"
              onClick={() => setSelectedPatch(patch.id)}
              aria-pressed={isSelected}
              aria-label={patch.name}
              title={patch.name}
              className={`w-8 h-8 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                isSelected
                  ? "border-brand-navy bg-white"
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              {patch.image ? (
                <Image src={patch.image} alt="" width={18} height={18} className="w-[18px] h-[18px] object-contain" />
              ) : (
                <X className="w-3.5 h-3.5 text-gray-300" aria-hidden="true" />
              )}
            </button>
          )
        })}
      </div>

      {error && <p className="text-[11px] text-brand-accent">{error}</p>}

      <button
        type="button"
        onClick={handleUpdate}
        disabled={saving}
        className="w-full py-2 bg-brand-navy text-white text-xs font-semibold rounded-lg hover:bg-brand-primary-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
      >
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        Update Customization
      </button>
    </div>
  )
}
